import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, Trash2, UserPlus, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CRM_STAGES, CRM_PLATFORMS, CRM_CONTACT_TYPES, COUNTRY_OPTIONS } from "@/lib/constants";
import { useLang } from "@/i18n/LanguageContext";
import { EuBadge } from "@/components/admin/EuBadge";

const FIELD = "w-full bg-transparent border border-aura-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";
const LABEL = "block text-xs uppercase tracking-[0.1em] text-zinc-500 mb-1.5";

export function CrmSheet({ lead, open, onOpenChange, onSaved, onDeleted }) {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [convertEmail, setConvertEmail] = useState("");
  const [convertPwd, setConvertPwd] = useState("");
  const [converting, setConverting] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    if (lead) setForm({ ...lead, contact_type: lead.contact_type || "creator", content_links_text: (lead.content_links || []).join("\n") });
  }, [lead]);

  if (!form) return null;
  const isNew = !form.id;
  const type = form.contact_type || "creator";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const payload = () => ({
    name: form.name, contact_type: type, email: form.email || "", phone: form.phone || "", country: form.country || "", handle: form.handle || "", platform: form.platform || "instagram", profile_url: form.profile_url || "",
    stage: form.stage || "to_contact", contacted_at: form.contacted_at || "", responded: !!form.responded, response_notes: form.response_notes || "",
    address: form.address || "", shipped: !!form.shipped, shipped_at: form.shipped_at || "", tracking_number: form.tracking_number || "",
    content_links: (form.content_links_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
    result_views: parseInt(form.result_views || 0, 10) || 0, result_sales: parseFloat(form.result_sales || 0) || 0, result_notes: form.result_notes || "", notes: form.notes || "",
    company_name: form.company_name || "", vat_number: form.vat_number || "", store_address: form.store_address || "", deal_type: form.deal_type || "",
    is_partner: !!form.is_partner, logo_url: form.logo_url || "",
    order_value: parseFloat(form.order_value || 0) || 0, order_reference: form.order_reference || "",
  });

  const save = async () => {
    if (!form.name) { toast.error(t("crm.name")); return; }
    setSaving(true);
    try {
      const { data } = isNew
        ? await api.post("/admin/crm/leads", payload())
        : await api.patch(`/admin/crm/leads/${form.id}`, payload());
      toast.success(isNew ? t("crm.created") : t("crm.saved"));
      onSaved(data, isNew);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(t("crm.deleteConfirm"))) return;
    try { await api.delete(`/admin/crm/leads/${form.id}`); toast.success(t("crm.deleted")); onDeleted(form.id); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendInvite = async () => {
    setSendingInvite(true);
    try {
      const { data } = await api.post(`/admin/crm/leads/${form.id}/send-invite`);
      toast.success(`${t("crm.inviteSent")} ${data.sent_to}`);
      setForm((f) => ({ ...f, invite_email_sent: true }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSendingInvite(false); }
  };

  const convert = async () => {
    if (!convertEmail) { toast.error(t("crm.convertEmail")); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/admin/crm/leads/${form.id}/convert`, { email: convertEmail, password: convertPwd || null });
      toast.success(`${t("crm.convertedMsg")} ${data.temp_password}`, { duration: 12000 });
      setForm((f) => ({ ...f, converted_creator_id: data.creator.id, stage: "done" }));
      onSaved({ ...form, converted_creator_id: data.creator.id, stage: "done" }, false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="crm-sheet" className="bg-[#0a0a0a] border-aura-border text-white w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="text-white font-display text-2xl">{isNew ? t("crm.new") : form.name}</SheetTitle></SheetHeader>

        <div className="mt-6 space-y-5 pb-10">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className={LABEL}>{t("crm.name")}</label><input data-testid="crm-name" className={FIELD} value={form.name || ""} onChange={set("name")} /></div>
            <div><label className={LABEL}>{t("crm.contactType")}</label>
              <select data-testid="crm-type" className={FIELD} value={type} onChange={set("contact_type")}>
                {CRM_CONTACT_TYPES.map((tp) => <option key={tp} value={tp} className="bg-[#0a0a0a]">{t(`crm.types.${tp}`)}</option>)}
              </select>
            </div>
            <div><label className={LABEL}>{t("crm.stage")}</label>
              <select data-testid="crm-stage" className={FIELD} value={form.stage || "to_contact"} onChange={set("stage")}>
                {CRM_STAGES.map((s) => <option key={s} value={s} className="bg-[#0a0a0a]">{t(`crm.stages.${s}`)}</option>)}
              </select>
            </div>
          </div>

          {(type === "creator" || type === "store") && (
            <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
              <div><label className={LABEL}>{t("crm.email")}</label><input data-testid="crm-email" type="email" className={FIELD} value={form.email || ""} onChange={set("email")} placeholder="nome@email.com" /></div>
              <div><label className={LABEL}>{t("crm.phone")}</label><input data-testid="crm-phone" className={FIELD} value={form.phone || ""} onChange={set("phone")} placeholder="+39 ..." /></div>
              {!isNew && (
                <div className="col-span-2">
                  <button type="button" data-testid="crm-send-invite-btn" onClick={sendInvite} disabled={sendingInvite || !form.email}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-sm text-zinc-200 hover:border-white/50 hover:text-white py-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    {sendingInvite ? <><Loader2 size={15} className="animate-spin" /> {t("crm.inviteSending")}</> : <><Mail size={15} /> {t("crm.sendInvite")}</>}
                  </button>
                  {form.invite_email_sent && <p className="text-xs text-emerald-400 mt-1.5">{t("crm.inviteAlready")}</p>}
                  <p className="text-xs text-zinc-500 mt-1">{t("crm.sendInviteHint")}</p>
                </div>
              )}
            </div>
          )}

          {type === "creator" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={LABEL}>{t("crm.platform")}</label>
                  <select data-testid="crm-platform" className={FIELD} value={form.platform || "instagram"} onChange={set("platform")}>
                    {CRM_PLATFORMS.map((p) => <option key={p} value={p} className="bg-[#0a0a0a]">{t(`crm.platforms.${p}`)}</option>)}
                  </select>
                </div>
                <div><label className={LABEL}>{t("crm.contactedAt")}</label><input type="date" className={FIELD} value={form.contacted_at || ""} onChange={set("contacted_at")} /></div>
                <div className="col-span-2"><label className={LABEL}>{t("crm.profileUrl")}</label><input data-testid="crm-profile-url" className={FIELD} value={form.profile_url || ""} onChange={set("profile_url")} placeholder="https://..." /></div>
                <label className="flex items-center gap-2 cursor-pointer"><input data-testid="crm-responded" type="checkbox" checked={!!form.responded} onChange={setBool("responded")} className="h-4 w-4 accent-emerald-400" /><span className="text-sm text-zinc-200">{t("crm.responded")}</span></label>
              </div>
              <div><label className={LABEL}>{t("crm.responseNotes")}</label><textarea rows={2} className={FIELD} value={form.response_notes || ""} onChange={set("response_notes")} /></div>
            </>
          )}

          {type === "store" && (
            <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs uppercase tracking-wide text-zinc-400">{t("crm.storeSection")}</div>
              <div className="col-span-2"><label className={LABEL}>{t("crm.companyName")}</label><input data-testid="crm-company-name" className={FIELD} value={form.company_name || ""} onChange={set("company_name")} /></div>
              <div><label className={LABEL}>{t("crm.vatNumber")}</label><input data-testid="crm-vat" className={FIELD} value={form.vat_number || ""} onChange={set("vat_number")} /></div>
              <div><label className={LABEL}>{t("crm.dealType")}</label><input data-testid="crm-deal-type" className={FIELD} value={form.deal_type || ""} onChange={set("deal_type")} /></div>
              <div className="col-span-2"><label className={LABEL}>{t("crm.storeAddress")}</label><input data-testid="crm-store-address" className={FIELD} value={form.store_address || ""} onChange={set("store_address")} /></div>
              <div className="col-span-2 border-t border-white/10 pt-3">
                <label className="flex items-center gap-2 cursor-pointer"><input data-testid="crm-is-partner" type="checkbox" checked={!!form.is_partner} onChange={setBool("is_partner")} className="h-4 w-4 accent-amber-400" /><span className="text-sm text-zinc-200">{t("crm.isPartner")}</span></label>
                <p className="text-xs text-zinc-500 mt-1">{t("crm.isPartnerHint")}</p>
              </div>
              <div className="col-span-2"><label className={LABEL}>{t("crm.logoUrl")}</label><input data-testid="crm-logo-url" className={FIELD} value={form.logo_url || ""} onChange={set("logo_url")} placeholder="https://.../logo.png" /></div>
              <div><label className={LABEL}>{t("crm.contactedAt")}</label><input type="date" className={FIELD} value={form.contacted_at || ""} onChange={set("contacted_at")} /></div>
              <label className="flex items-center gap-2 cursor-pointer mt-6"><input type="checkbox" checked={!!form.responded} onChange={setBool("responded")} className="h-4 w-4 accent-emerald-400" /><span className="text-sm text-zinc-200">{t("crm.responded")}</span></label>
            </div>
          )}

          {type === "customer" && (
            <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs uppercase tracking-wide text-zinc-400">{t("crm.customerSection")}</div>
              <div><label className={LABEL}>{t("crm.orderValue")}</label><input data-testid="crm-order-value" type="number" className={FIELD} value={form.order_value ?? ""} onChange={set("order_value")} /></div>
              <div><label className={LABEL}>{t("crm.orderReference")}</label><input data-testid="crm-order-ref" className={FIELD} value={form.order_reference || ""} onChange={set("order_reference")} /></div>
              <div><label className={LABEL}>{t("crm.contactedAt")}</label><input type="date" className={FIELD} value={form.contacted_at || ""} onChange={set("contacted_at")} /></div>
              <label className="flex items-center gap-2 cursor-pointer mt-6"><input type="checkbox" checked={!!form.responded} onChange={setBool("responded")} className="h-4 w-4 accent-emerald-400" /><span className="text-sm text-zinc-200">{t("crm.responded")}</span></label>
            </div>
          )}

          <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>{t("crm.country")}</label>
              <div className="flex items-center gap-3">
                <select data-testid="crm-country" className={FIELD} value={form.country || ""} onChange={set("country")}>
                  <option value="" className="bg-[#0a0a0a]">{t("crm.selectCountry")}</option>
                  {COUNTRY_OPTIONS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
                <EuBadge country={form.country} />
              </div>
            </div>
            <div className="col-span-2"><label className={LABEL}>{t("crm.address")}</label><input data-testid="crm-address" className={FIELD} value={form.address || ""} onChange={set("address")} /></div>
            <label className="flex items-center gap-2 cursor-pointer"><input data-testid="crm-shipped" type="checkbox" checked={!!form.shipped} onChange={setBool("shipped")} className="h-4 w-4 accent-emerald-400" /><span className="text-sm text-zinc-200">{t("crm.shipped")}</span></label>
            <div><label className={LABEL}>{t("crm.shippedAt")}</label><input type="date" className={FIELD} value={form.shipped_at || ""} onChange={set("shipped_at")} /></div>
            <div className="col-span-2"><label className={LABEL}>{t("crm.tracking")}</label><input className={FIELD} value={form.tracking_number || ""} onChange={set("tracking_number")} /></div>
          </div>

          {type === "creator" && (
            <div className="border-t border-white/10 pt-4">
              <label className={LABEL}>{t("crm.contentLinks")}</label><textarea rows={2} className={FIELD} value={form.content_links_text} onChange={set("content_links_text")} placeholder="https://..." />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><label className={LABEL}>{t("crm.resultViews")}</label><input data-testid="crm-views" type="number" className={FIELD} value={form.result_views ?? ""} onChange={set("result_views")} /></div>
                <div><label className={LABEL}>{t("crm.resultSales")}</label><input data-testid="crm-sales" type="number" className={FIELD} value={form.result_sales ?? ""} onChange={set("result_sales")} /></div>
              </div>
              <div className="mt-3"><label className={LABEL}>{t("crm.resultNotes")}</label><textarea rows={2} className={FIELD} value={form.result_notes || ""} onChange={set("result_notes")} /></div>
            </div>
          )}

          <div><label className={LABEL}>{t("crm.notes")}</label><textarea data-testid="crm-notes" rows={2} className={FIELD} value={form.notes || ""} onChange={set("notes")} /></div>

          <div className="flex items-center gap-3">
            <button data-testid="crm-save-btn" onClick={save} disabled={saving} className="flex-1 rounded-full bg-white text-black font-semibold py-3 hover:bg-zinc-200 active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <><Loader2 size={16} className="animate-spin" /> {t("crm.saving")}</> : (isNew ? t("crm.create") : t("crm.save"))}
            </button>
            {!isNew && <button data-testid="crm-delete-btn" onClick={remove} className="rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 p-3 transition-colors"><Trash2 size={18} /></button>}
          </div>

          {!isNew && type === "creator" && (
            <div className="border-t border-white/10 pt-5">
              <div className="flex items-center gap-2 mb-2"><UserPlus size={16} className="text-emerald-300" /><h3 className="font-display text-sm font-semibold text-white">{t("crm.convertTitle")}</h3></div>
              {form.converted_creator_id ? (
                <p className="text-sm text-emerald-300">{t("crm.alreadyConverted")}</p>
              ) : (
                <>
                  <p className="text-xs text-zinc-500 mb-3">{t("crm.convertDesc")}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="crm-convert-email" type="email" className={FIELD} value={convertEmail} onChange={(e) => setConvertEmail(e.target.value)} placeholder={t("crm.convertEmail")} />
                    <input data-testid="crm-convert-pwd" className={FIELD} value={convertPwd} onChange={(e) => setConvertPwd(e.target.value)} placeholder={t("crm.convertPwd")} />
                  </div>
                  <button data-testid="crm-convert-btn" onClick={convert} disabled={converting} className="mt-3 w-full rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {converting ? <><Loader2 size={15} className="animate-spin" /> {t("crm.converting")}</> : t("crm.convertBtn")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
